import PropTypes from 'prop-types'

/**
 * A clean table: a header row in small caps over an ink-line rule, hairline
 * dividers between rows, columns with `numeric: true` right-aligned in
 * tabular figures. `stub` sets the first column as the row's key (tabular,
 * medium weight).
 */
export default function Ledger({
  columns,
  rows,
  stub = false,
  caption,
  rowKey = row => row.id,
  tone = 'soft',
  className = '',
}) {
  const headTone = tone === 'ink' ? 'text-ink' : 'text-ink-soft'
  const cellClass = (col, i, numericClass) =>
    [
      'pr-4 last:pr-0',
      col.numeric ? `text-right ${numericClass}` : '',
      stub && i === 0 ? 'font-medium tabular' : '',
      col.className || '',
    ].join(' ')

  return (
    <table className={`w-full border-collapse ${className}`}>
      {caption && <caption className="sr-only">{caption}</caption>}
      <thead>
        <tr>
          {columns.map((col, i) => (
            <th
              key={col.key}
              scope="col"
              className={`label text-xs ${headTone} text-left pb-2 align-bottom border-b border-ink-line ${cellClass(col, i, '')}`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr
            key={rowKey(row)}
            className="border-b border-ink-hair last:border-b-0"
          >
            {columns.map((col, i) => (
              <td
                key={col.key}
                className={`py-2.5 leading-pitch align-baseline ${cellClass(col, i, 'tabular')}`}
              >
                {col.render ? col.render(row) : row[col.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

Ledger.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      numeric: PropTypes.bool,
      render: PropTypes.func,
      className: PropTypes.string,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  stub: PropTypes.bool,
  caption: PropTypes.string,
  rowKey: PropTypes.func,
  // Header ink: `ink` on amber or tinted grounds, where soft ink fails AA.
  tone: PropTypes.oneOf(['soft', 'ink']),
  className: PropTypes.string,
}
